/**
 * client/onboarding.ts
 * Responsabilidad: Lógica de onboarding del jugador (tooltip inicial, counters elementales) y persistencia del tutorial.
 *
 * Deriva las relaciones elementales dinámicamente desde ELEMENTAL_COUNTERS para evitar duplicar la tabla de counters.
 */
import { ELEMENTAL_COUNTERS } from "../src/shared/constants";
import { t } from "./i18n";

/** Clave de localStorage para recordar si el tutorial ya se vio */
export const TUTORIAL_SEEN_KEY = "alchem_tutorial_seen";

/** Relaciones elementales de un jugador */
export interface ElementCounters {
    /** Elementos a los que este elemento vence */
    hunts: string[];
    /** Elementos que vencen a este elemento */
    fleesFrom: string[];
}

/**
 * Deriva las relaciones elementales de un elemento consultando ELEMENTAL_COUNTERS.
 * Dinámico: si se añaden nuevos counters, esta función se actualiza sola.
 */
export function getElementCounters(element: string): ElementCounters {
    const hunts: string[] = [];
    const fleesFrom: string[] = [];
    for (const key of Object.keys(ELEMENTAL_COUNTERS)) {
        const parts = key.split("->");
        if (parts.length !== 2) continue;
        const [attacker, defender] = parts;
        if (attacker === element) hunts.push(defender);
        if (defender === element) fleesFrom.push(attacker);
    }
    return { hunts, fleesFrom };
}

/**
 * Devuelve el mensaje localizado para cada paso del tutorial.
 */
export function getTutorialStepMessage(step: string, foodCount: number = 0): string {
    switch (step) {
        case "identity":
            return t("tutorial.identity", { defaultValue: t("tutorial.step1") });
        case "movement":
            return t("tutorial.movement", { defaultValue: t("tutorial.step2") });
        case "growth":
            return t("tutorial.growth", { count: foodCount, defaultValue: `Absorb energy orbs to grow (${foodCount}/3).` });
        case "danger":
            return t("tutorial.danger", { defaultValue: t("tutorial.step3") });
        default:
            return "";
    }
}

/**
 * Devuelve la etiqueta localizada para el encabezado del tutorial (ej. "STEP 1/4" / "PASO 1/4").
 */
export function getTutorialStepLabel(step: string): string {
    const stepNumber = step === "identity" ? 1 : step === "movement" ? 2 : step === "growth" ? 3 : step === "danger" ? 4 : 1;
    return t("tutorial.stepLabel", { current: stepNumber, total: 4, defaultValue: `STEP ${stepNumber}/4` });
}

/**
 * Devuelve true si el jugador ya vio el tutorial (según localStorage).
 */
export function hasSeenTutorial(): boolean {
    try {
        return localStorage.getItem(TUTORIAL_SEEN_KEY) === "true";
    } catch {
        return false;
    }
}

/**
 * Marca el tutorial como visto en localStorage.
 */
export function markTutorialSeen(): void {
    try {
        localStorage.setItem(TUTORIAL_SEEN_KEY, "true");
    } catch {
        // localStorage puede no estar disponible (modo privado en algunos navegadores)
    }
}
